/**
 * Rocket.Chat Client Service
 * Shared between web and mobile — handles REST API + WebSocket.
 * URL is configured at runtime via configure().
 */

// ============================================================
// TYPES
// ============================================================

export interface RCUser {
  _id: string;
  username: string;
  name: string;
  status: string;
  avatarUrl?: string;
}

export interface RCMessage {
  _id: string;
  rid: string;
  msg: string;
  ts: { $date: number } | string;
  u: {
    _id: string;
    username: string;
    name?: string;
  };
  attachments?: any[];
  editedAt?: string;
}

export interface RCRoom {
  _id: string;
  name?: string;
  fname?: string;
  t: 'c' | 'p' | 'd';
  msgs: number;
  usersCount: number;
  lm?: string;
  topic?: string;
  description?: string;
  usernames?: string[];
  /** Per-room unread count, merged in from the user's subscriptions. */
  unread?: number;
}

export interface RCAuthResult {
  userId: string;
  authToken: string;
  me: RCUser;
}

// ============================================================
// SERVICE
// ============================================================

class RocketChatService {
  private authToken: string | null = null;
  private userId: string | null = null;
  private ws: WebSocket | null = null;
  private wsCallbacks: Map<string, (data: any) => void> = new Map();
  private messageListeners: Map<string, (message: RCMessage) => void> = new Map();
  private reconnectTimer: any = null;
  private reconnectAttempts = 0;
  private subIdCounter = 0;
  private authListeners: Set<() => void> = new Set();
  // Realtime (DDP) subscription bookkeeping. A room sub only delivers once the
  // socket has completed its `login` method, and every sub is lost across a
  // reconnect — so we track login state + the id of the login call, and re-issue
  // all room subs whenever login completes (first connect AND after any drop).
  private wsLoggedIn = false;
  private wsLoginId: string | null = null;
  private roomSubIds: Map<string, string> = new Map();
  // Unread-count bookkeeping. Seeded from /subscriptions.get and kept live by the
  // per-user `stream-notify-user/<userId>/subscriptions-changed` DDP events, so
  // the dashboard unread horn updates in real time (not just on the 15s poll),
  // works even when no room is open, and re-seeds after every reconnect.
  private roomUnread: Map<string, number> = new Map();
  private unreadListeners: Set<() => void> = new Set();

  private baseUrl = '';
  private apiBase = '';
  private wsUrl = '';

  /**
   * Configure the RC server URL. Must be called before any other method.
   */
  configure(url: string) {
    this.baseUrl = url.replace(/\/$/, '');
    this.apiBase = `${this.baseUrl}/api/v1`;
    this.wsUrl = this.baseUrl.replace(/^http/, 'ws') + '/websocket';
  }

  isConfigured(): boolean {
    return this.baseUrl.length > 0;
  }

  // ----------------------------------------------------------
  // Auth headers
  // ----------------------------------------------------------
  private get headers() {
    return {
      'Content-Type': 'application/json',
      'X-Auth-Token': this.authToken || '',
      'X-User-Id': this.userId || '',
    };
  }

  private async apiCall(method: string, endpoint: string, body?: any): Promise<any> {
    // 15s timeout — prevents UI freeze when RC server is slow/unreachable
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 15000);
    let res: Response;
    try {
      res = await fetch(`${this.apiBase}${endpoint}`, {
        method,
        headers: this.headers,
        body: body ? JSON.stringify(body) : undefined,
        signal: controller.signal,
      });
    } catch (err: any) {
      clearTimeout(timer);
      if (err?.name === 'AbortError') {
        throw new Error(`Rocket.Chat request timed out (${method} ${endpoint})`);
      }
      throw new Error(err?.message || `Rocket.Chat request failed (${method} ${endpoint})`);
    }
    clearTimeout(timer);
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(data.error || data.message || 'API call failed');
    }
    return data;
  }

  // ============================================================
  // AUTH
  // ============================================================

  /**
   * Login with username + password (used only if direct login is needed).
   */
  async login(username: string, password: string): Promise<RCAuthResult> {
    const res = await fetch(`${this.apiBase}/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ user: username, password }),
    });
    const data = await res.json();
    if (data.status === 'success') {
      this.authToken = data.data.authToken;
      this.userId = data.data.userId;
      this.notifyAuthChange();
      return {
        userId: data.data.userId,
        authToken: data.data.authToken,
        me: data.data.me,
      };
    }
    throw new Error(data.message || 'Login failed');
  }

  /**
   * Restore session from a token (obtained via backend /chat/token endpoint).
   */
  restoreSession(authToken: string, userId: string) {
    this.authToken = authToken;
    this.userId = userId;
    this.notifyAuthChange();
  }

  /**
   * Subscribe to auth-state changes (login / restoreSession / logout). Returns an
   * unsubscribe function. This is what lets UI react when a session is restored
   * ASYNCHRONOUSLY after the component already mounted — otherwise a panel that
   * reads isAuthenticated() once at first render stays stuck on that stale value.
   */
  onAuthChange(listener: () => void): () => void {
    this.authListeners.add(listener);
    return () => {
      this.authListeners.delete(listener);
    };
  }

  private notifyAuthChange() {
    for (const l of this.authListeners) {
      try {
        l();
      } catch {
        // a bad listener must never break auth state propagation
      }
    }
  }

  getAuthToken(): string | null {
    return this.authToken;
  }

  getUserId(): string | null {
    return this.userId;
  }

  isAuthenticated(): boolean {
    return !!this.authToken && !!this.userId;
  }

  async logout() {
    try {
      if (this.authToken) await this.apiCall('POST', '/logout');
    } catch {
      // ignore logout errors
    }
    this.authToken = null;
    this.userId = null;
    this.disconnectWebSocket();
    this.notifyAuthChange();
  }

  async me(): Promise<RCUser> {
    return this.apiCall('GET', '/me');
  }

  // ============================================================
  // ROOMS
  // ============================================================

  async getChannels(): Promise<RCRoom[]> {
    const data = await this.apiCall('GET', '/channels.list.joined?count=100');
    return data.channels;
  }

  async getGroups(): Promise<RCRoom[]> {
    const data = await this.apiCall('GET', '/groups.list?count=100');
    return data.groups;
  }

  async getDirectMessages(): Promise<RCRoom[]> {
    const data = await this.apiCall('GET', '/dm.list?count=100');
    return data.ims;
  }

  async getAllRooms(): Promise<RCRoom[]> {
    const data = await this.apiCall('GET', '/rooms.get');
    return data.update || [];
  }

  /** Mark a room's messages as read for the current user, so the unread count
   *  (and the navbar badge) clears. Without this RC keeps the messages unread
   *  forever. Best-effort. */
  async markRead(roomId: string): Promise<void> {
    if (!this.isAuthenticated() || !roomId) return;
    try {
      await this.apiCall('POST', '/subscriptions.read', { rid: roomId });
    } catch {
      /* best-effort */
    }
  }

  /** The current user's subscriptions (one per room) — includes per-room `unread`,
   *  `rid`, and `open` (false once the conversation is closed/hidden via
   *  im.close/groups.close/channels.close). Returns [] when not authenticated or
   *  on error (best-effort). */
  async getSubscriptions(): Promise<Array<{ rid: string; unread: number; alert?: boolean; open: boolean }>> {
    if (!this.isAuthenticated()) return [];
    try {
      const data = await this.apiCall('GET', '/subscriptions.get');
      const subs: any[] = data?.update || [];
      const mapped = subs.map((s) => ({ rid: s.rid, unread: typeof s.unread === 'number' ? s.unread : 0, alert: !!s.alert, open: s.open !== false }));
      // Reseed the unread cache from the authoritative server list so the cached
      // getUnreadTotal() and the stream-driven updates stay in sync.
      this.roomUnread.clear();
      for (const m of mapped) this.roomUnread.set(m.rid, m.unread);
      return mapped;
    } catch {
      return [];
    }
  }

  /** Total unread message count across all of the current user's subscriptions.
   *  Used to badge the chat launcher and trigger a notification sound. Returns 0
   *  when not authenticated or on any error (best-effort). */
  async getTotalUnread(): Promise<number> {
    const subs = await this.getSubscriptions();
    return subs.reduce((sum, s) => sum + (s.unread || 0), 0);
  }

  async createDirectMessage(username: string): Promise<RCRoom> {
    const data = await this.apiCall('POST', '/dm.create', { username });
    return data.room;
  }

  /** Remove a conversation from the current user's list. Uses the RC "close"
   *  endpoint for the room's type (hides it without destroying history — it
   *  reappears if a new message arrives). Best-effort across the three room
   *  types so callers don't need to know which one it is. */
  async closeConversation(roomId: string, roomType?: 'c' | 'p' | 'd'): Promise<void> {
    if (!roomId) return;
    const endpoints =
      roomType === 'd' ? ['im.close']
      : roomType === 'p' ? ['groups.close']
      : roomType === 'c' ? ['channels.close']
      : ['im.close', 'groups.close', 'channels.close'];
    for (const ep of endpoints) {
      try {
        await this.apiCall('POST', `/${ep}`, { roomId });
        return;
      } catch {
        /* wrong type for this endpoint — try the next */
      }
    }
  }

  // ============================================================
  // MESSAGES
  // ============================================================

  async getMessages(roomId: string, count = 50, oldest?: string): Promise<RCMessage[]> {
    const oldestParam = oldest ? `&oldest=${oldest}` : '';
    // The history endpoint differs by room type: channels.history (public
    // channels), groups.history (private groups), im.history (DIRECT messages).
    // Try each in turn so DMs — the common team-to-team case — actually load
    // their history instead of coming back empty.
    for (const ep of ['channels.history', 'groups.history', 'im.history']) {
      try {
        const data = await this.apiCall('GET', `/${ep}?roomId=${roomId}&count=${count}${oldestParam}`);
        if (Array.isArray(data?.messages)) return data.messages;
      } catch {
        /* wrong room type for this endpoint — try the next */
      }
    }
    return [];
  }

  async sendMessage(roomId: string, text: string): Promise<RCMessage> {
    const data = await this.apiCall('POST', '/chat.sendMessage', {
      message: { rid: roomId, msg: text },
    });
    return data.message;
  }

  async deleteMessage(messageId: string, roomId: string) {
    return this.apiCall('POST', '/chat.delete', { msgId: messageId, roomId });
  }

  // ============================================================
  // USER INFO
  // ============================================================

  getAvatarUrl(username: string): string {
    return `${this.baseUrl}/avatar/${username}`;
  }

  // ============================================================
  // WEBSOCKET — REAL-TIME MESSAGING
  // ============================================================

  connectWebSocket() {
    if (!this.wsUrl) throw new Error('RocketChatService not configured — call configure() first');
    // Skip if a socket is already OPEN or still CONNECTING — otherwise we'd
    // abandon an in-flight socket and orphan its handlers.
    if (this.ws && (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)) return;

    this.ws = new WebSocket(this.wsUrl);

    this.ws.onopen = () => {
      this.reconnectAttempts = 0;
      this.wsSend({ msg: 'connect', version: '1', support: ['1'] });
    };

    this.ws.onmessage = (event) => {
      const data = JSON.parse(event.data);

      if (data.msg === 'ping') {
        this.wsSend({ msg: 'pong' });
        return;
      }

      if (data.msg === 'connected') {
        this.wsLoggedIn = false;
        this.wsLogin();
        return;
      }

      // DDP login result: mark the socket authenticated and (re)subscribe every
      // open room. This is what makes realtime survive reconnects and rooms that
      // were opened before login finished (the "messages only appear after
      // leaving and re-entering the chat" bug).
      if (data.msg === 'result' && data.id && data.id === this.wsLoginId) {
        if (!data.error) {
          this.wsLoggedIn = true;
          this.resubscribeRooms();
          this.subscribeUserStreams();
          // Seed the unread cache from the server on every (re)login so the horn
          // is correct immediately after connect/reconnect, then push-driven from
          // here on.
          this.getSubscriptions().then(() => this.notifyUnreadChange()).catch(() => {});
        }
        return;
      }

      if (data.msg === 'changed' && data.collection === 'stream-room-messages') {
        const message: RCMessage = data.fields.args[0];
        const listener = this.messageListeners.get(message.rid);
        if (listener) listener(message);
        const allListener = this.messageListeners.get('__all__');
        if (allListener) allListener(message);
        return;
      }

      // Per-user unread stream: RC pushes [action, subscription] whenever a
      // message lands in (or a room is read in) ANY of the user's rooms — even
      // rooms that aren't open. Update the cached unread and notify listeners
      // (the dashboard horn) in real time.
      if (data.msg === 'changed' && data.collection === 'stream-notify-user') {
        const args = data.fields?.args;
        if (Array.isArray(args) && args.length >= 2) {
          const action = args[0];
          const sub = args[1];
          if (sub?.rid) {
            if (action === 'removed') this.roomUnread.delete(sub.rid);
            else this.roomUnread.set(sub.rid, typeof sub.unread === 'number' ? sub.unread : 0);
          }
        }
        this.notifyUnreadChange();
        return;
      }

      if (data.id && this.wsCallbacks.has(data.id)) {
        const cb = this.wsCallbacks.get(data.id)!;
        this.wsCallbacks.delete(data.id);
        cb(data);
      }
    };

    this.ws.onclose = () => {
      // Socket dropped — every DDP sub is now dead. Mark logged-out so the next
      // successful login re-issues all room subs (resubscribeRooms).
      this.wsLoggedIn = false;
      // Cap reconnect attempts with exponential backoff, max 10 attempts,
      // so a down RC server does not create an endless reconnect storm.
      if (this.reconnectAttempts >= 10) return;
      const delay = Math.min(30000, 3000 * Math.pow(1.5, this.reconnectAttempts));
      this.reconnectAttempts += 1;
      this.reconnectTimer = setTimeout(() => this.connectWebSocket(), delay);
    };

    this.ws.onerror = () => {
      this.ws?.close();
    };
  }

  private wsLogin() {
    if (!this.authToken) return;
    this.wsLoginId = this.nextId();
    this.wsSend({
      msg: 'method',
      method: 'login',
      id: this.wsLoginId,
      params: [{ resume: this.authToken }],
    });
  }

  /** (Re)subscribe to every room that currently has a listener. Runs after the
   *  DDP login result arrives — on first connect AND after any reconnect — so a
   *  socket drop or a room opened before login completes never leaves the view
   *  without live updates. */
  private resubscribeRooms() {
    for (const roomId of this.messageListeners.keys()) {
      if (roomId === '__all__') continue;
      this.sendRoomSub(roomId);
    }
  }

  /** Subscribe to the per-user unread stream. Called after every DDP login so
   *  the dashboard horn gets real-time unread updates for any room (not just
   *  open ones) and the subscription is re-established after reconnects. */
  private subscribeUserStreams() {
    if (!this.userId) return;
    this.wsSend({
      msg: 'sub',
      id: this.nextId(),
      name: 'stream-notify-user',
      params: [`${this.userId}/subscriptions-changed`, false],
    });
  }

  /** Subscribe to unread-total changes (new message in any room, or a room read).
   *  Returns an unsubscribe fn. Consumers read getUnreadTotal() for the new sum. */
  onUnreadChange(listener: () => void): () => void {
    this.unreadListeners.add(listener);
    return () => {
      this.unreadListeners.delete(listener);
    };
  }

  private notifyUnreadChange() {
    for (const l of this.unreadListeners) {
      try {
        l();
      } catch {
        // a bad listener must never break unread propagation
      }
    }
  }

  /** Cached total unread across all of the user's rooms — kept live by the
   *  stream-notify-user events, so it's available synchronously without a REST
   *  round-trip. Falls back to 0 before the first seed. */
  getUnreadTotal(): number {
    let sum = 0;
    for (const n of this.roomUnread.values()) sum += n;
    return sum;
  }

  private sendRoomSub(roomId: string) {
    const subId = this.nextId();
    this.roomSubIds.set(roomId, subId);
    this.wsSend({
      msg: 'sub',
      id: subId,
      name: 'stream-room-messages',
      params: [roomId, false],
    });
  }

  subscribeToRoom(roomId: string, onMessage: (message: RCMessage) => void): () => void {
    this.messageListeners.set(roomId, onMessage);
    if (this.wsLoggedIn) {
      // Socket already authenticated — subscribe right now.
      this.sendRoomSub(roomId);
    } else {
      // Not logged in yet (fresh launch, or mid-reconnect): make sure a socket is
      // being established. resubscribeRooms() will issue this sub once login
      // completes, so the listener is already registered above.
      this.connectWebSocket();
    }
    return () => {
      this.messageListeners.delete(roomId);
      const subId = this.roomSubIds.get(roomId);
      if (subId) {
        this.wsSend({ msg: 'unsub', id: subId });
        this.roomSubIds.delete(roomId);
      }
    };
  }

  onAnyMessage(callback: (message: RCMessage) => void): () => void {
    this.messageListeners.set('__all__', callback);
    return () => this.messageListeners.delete('__all__');
  }

  sendTyping(roomId: string, username: string, isTyping: boolean) {
    this.wsSend({
      msg: 'method',
      method: 'stream-notify-room',
      id: this.nextId(),
      params: [`${roomId}/typing`, username, isTyping],
    });
  }

  disconnectWebSocket() {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.ws) {
      this.ws.onclose = null;
      this.ws.close();
      this.ws = null;
    }
    this.messageListeners.clear();
    this.wsCallbacks.clear();
    this.roomSubIds.clear();
    this.roomUnread.clear();
    this.wsLoggedIn = false;
    this.wsLoginId = null;
  }

  private wsSend(data: any) {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(data));
    }
  }

  private nextId(): string {
    return String(++this.subIdCounter);
  }
}

// Export singleton
export const rocketChat = new RocketChatService();
export default rocketChat;
